import { useState, useRef, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  Divider,
  Switch,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

interface SystemSettingsData {
  applicationName: string;
  supportEmail: string;
  maxUploadSize: number;
  sessionTimeout: number;
  maintenanceMode: boolean;
  allowRegistration: boolean;
  requireEmailVerification: boolean;
}

const DEFAULT_SETTINGS: SystemSettingsData = {
  applicationName: 'Risikostyring',
  supportEmail: 'kristian.aaen.nordhaug@avinor.no',
  maxUploadSize: 10,
  sessionTimeout: 60,
  maintenanceMode: false,
  allowRegistration: true,
  requireEmailVerification: false,
};

export const SystemSettings = () => {
  const { t } = useTranslation();
  const initializedRef = useRef(false);
  const successTimeoutRef = useRef<ReturnType<
    typeof globalThis.setTimeout
  > | null>(null);

  const [settings, setSettings] =
    useState<SystemSettingsData>(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Simulate loading settings
  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      // In a real implementation, this would fetch from the API
      globalThis.queueMicrotask(() => {
        setSettings(DEFAULT_SETTINGS);
        setIsLoading(false);
      });
    }
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        globalThis.clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  const handleChange = (
    field: keyof SystemSettingsData,
    value: string | number | boolean
  ) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);

    try {
      // In a real implementation, this would call the API
      await new Promise(resolve => globalThis.setTimeout(resolve, 1000));

      setHasChanges(false);
      setSaveSuccess(true);
      successTimeoutRef.current = globalThis.setTimeout(
        () => setSaveSuccess(false),
        3000
      );
    } catch {
      setSaveError(t('admin.settings.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component={RouterLink}
          to="/admin"
          underline="hover"
          color="inherit"
        >
          {t('admin.dashboard.title')}
        </Link>
        <Typography color="text.primary">
          {t('admin.settings.title')}
        </Typography>
      </Breadcrumbs>

      <Typography variant="h4" component="h1" gutterBottom>
        {t('admin.settings.title')}
      </Typography>

      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {t('admin.settings.saveSuccess')}
        </Alert>
      )}

      {saveError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {saveError}
        </Alert>
      )}

      {/* General Settings */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('admin.settings.general')}
        </Typography>

        <TextField
          fullWidth
          label={t('admin.settings.applicationName')}
          value={settings.applicationName}
          onChange={e => handleChange('applicationName', e.target.value)}
          margin="normal"
        />

        <TextField
          fullWidth
          label={t('admin.settings.supportEmail')}
          value={settings.supportEmail}
          onChange={e => handleChange('supportEmail', e.target.value)}
          margin="normal"
          type="email"
        />
      </Paper>

      {/* Security Settings */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('admin.settings.security')}
        </Typography>

        <TextField
          fullWidth
          label={t('admin.settings.sessionTimeout')}
          value={settings.sessionTimeout}
          onChange={e =>
            handleChange('sessionTimeout', parseInt(e.target.value, 10) || 0)
          }
          margin="normal"
          type="number"
          helperText={t('admin.settings.sessionTimeoutHint')}
        />

        <Divider sx={{ my: 2 }} />

        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={settings.allowRegistration}
                onChange={e =>
                  handleChange('allowRegistration', e.target.checked)
                }
              />
            }
            label={t('admin.settings.allowRegistration')}
          />
          <FormControlLabel
            control={
              <Switch
                checked={settings.requireEmailVerification}
                onChange={e =>
                  handleChange('requireEmailVerification', e.target.checked)
                }
              />
            }
            label={t('admin.settings.requireEmailVerification')}
          />
        </FormGroup>
      </Paper>

      {/* Upload Settings */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('admin.settings.uploads')}
        </Typography>

        <TextField
          fullWidth
          label={t('admin.settings.maxUploadSize')}
          value={settings.maxUploadSize}
          onChange={e =>
            handleChange('maxUploadSize', parseInt(e.target.value, 10) || 0)
          }
          margin="normal"
          type="number"
          helperText={t('admin.settings.maxUploadSizeHint')}
        />
      </Paper>

      {/* Maintenance Mode */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('admin.settings.maintenance')}
        </Typography>

        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('admin.settings.maintenanceWarning')}
        </Alert>

        <FormControlLabel
          control={
            <Switch
              checked={settings.maintenanceMode}
              onChange={e => handleChange('maintenanceMode', e.target.checked)}
              color="warning"
            />
          }
          label={t('admin.settings.maintenanceMode')}
        />
      </Paper>

      {/* Save Button */}
      {hasChanges && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={isSaving}
            size="large"
          >
            {isSaving ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t('common.saveChanges')
            )}
          </Button>
        </Box>
      )}
    </Container>
  );
};
